/**
 * AdminWinnerSlotsPage — Winner Photo Slots management surface.
 *
 * Feb 2026 Winner Photo Slots dispatch. Presents the 10 canonical
 * winner slots (1..10) as a single-screen board so the operator can
 * populate / swap / remove ahead of the Aug 9 winner announcement.
 *
 * Route: /admin/contest/slots  (registered in App.js, tab in
 *   AdminContestTabs).
 *
 * UX contract (dispatch §Q2 answers + Feb 2026 Path (i) resolution):
 *   - Populate dropdown shows ALL entries — eligible + ineligible.
 *     Ineligible rows are greyed out with a small chip AND a
 *     tooltip explaining why. This surfaces the moderation-queue
 *     backlog + tamper/legacy-consent chase-list as a natural
 *     side-effect of the picker (rather than hiding it, which
 *     would lose signal).
 *   - Ineligibility chip label: "Not approved" OR
 *     "Consent missing". Approval is the earlier gate.
 *   - Each slot card renders a 1:1 (`aspect-square`) crop preview
 *     when populated, matching the public-side WinnerSlotCard
 *     compact variant so the operator sees exactly what visitors
 *     will see.
 *   - "Swap" action opens a mini-picker of the OTHER 9 slots.
 *
 * The consent gate reads `consent_given` — the SAME field the
 * submission endpoint enforces at
 * `backend/contest_entries.py:1425` (returns 400 if unchecked).
 * Every REAL entry in the DB necessarily has `consent_given=True`
 * because the submission path always sets it (see
 * `contest_entries.py:1611`). A "Consent missing" chip on the
 * picker therefore surfaces a TAMPERED, MIGRATED, or
 * TEST-INSERTED row — investigate before assigning.
 *
 * Server-side gate stays authoritative: the backend rejects a
 * `populate` attempt for an ineligible entry with `400
 * entry_not_eligible` + reason. Frontend gate is the UX layer;
 * server is the security layer.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowLeftRight, X as XIcon, Info } from "lucide-react";

import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import AdminContestTabs from "@/components/admin/AdminContestTabs";

// Rank-1 (and only rank-1) shows the trophy emoji in the badge on
// the ADMIN board so the operator's mental model matches the public
// site's badge behavior (rank-1 trophy anywhere it renders; 2-10
// plain "#N"). Compact public cards on pages 4-10 never render the
// trophy because those cards only host ranks 4-10 — same code path,
// rank-gated at render time.
function slotBadgeLabel(rank) {
  return rank === 1 ? `\u{1F3C6} #${rank}` : `#${rank}`;
}

export default function AdminWinnerSlotsPage() {
  const [slots, setSlots] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flight, setFlight] = useState(null); // {slot,action}

  // Picker state — { slotName, kind: 'populate' | 'swap' } | null.
  const [pickerOpen, setPickerOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [slotsRes, entriesRes] = await Promise.all([
        api.get("/admin/winner-slots"),
        api.get("/admin/winner-slots/eligible-entries"),
      ]);
      setSlots(slotsRes.data?.slots || []);
      setEntries(entriesRes.data?.entries || []);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Could not load slots.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handlePopulate(slotName, entryId) {
    setFlight({ slot: slotName, action: "populate" });
    try {
      await api.post(`/admin/winner-slots/${slotName}/populate`, {
        entry_id: entryId,
      });
      toast.success(`Slot ${slotName} populated.`);
      setPickerOpen(null);
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Populate failed.",
      );
    } finally {
      setFlight(null);
    }
  }

  async function handleRemove(slotName) {
    if (!window.confirm(`Remove the entry from ${slotName}?`)) return;
    setFlight({ slot: slotName, action: "remove" });
    try {
      const r = await api.delete(`/admin/winner-slots/${slotName}`);
      toast.success(
        r.data?.was_empty
          ? `Slot ${slotName} was already empty.`
          : `Slot ${slotName} cleared.`,
      );
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Remove failed.",
      );
    } finally {
      setFlight(null);
    }
  }

  async function handleSwap(slotA, slotB) {
    setFlight({ slot: slotA, action: "swap" });
    try {
      await api.post("/admin/winner-slots/swap", {
        slot_a: slotA,
        slot_b: slotB,
      });
      toast.success(`Swapped ${slotA} \u2194 ${slotB}.`);
      setPickerOpen(null);
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Swap failed.",
      );
    } finally {
      setFlight(null);
    }
  }

  const otherSlots = useMemo(() => {
    if (!pickerOpen || pickerOpen.kind !== "swap") return [];
    return slots.filter((s) => s.slot !== pickerOpen.slotName);
  }, [pickerOpen, slots]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="admin-winner-slots-page">
        <AdminContestTabs />
        <div
          className="flex items-center justify-center rounded-2xl border border-border bg-pp-surface p-10"
          data-testid="admin-winner-slots-loading"
        >
          <Loader2 className="h-5 w-5 animate-spin text-brand-orange" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-winner-slots-page">
      <AdminContestTabs />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-pp-text">
            Winner slots
          </h1>
          <p className="text-sm text-muted-foreground">
            The 10 canonical winner slots. Populate, swap, or clear
            for the winner announcement. Public site stays dark until
            a slot is populated.
          </p>
        </div>
      </div>

      <div
        className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5"
        data-testid="admin-winner-slots-grid"
      >
        {slots.map((s) => {
          const isEmpty = !s.occupant;
          const inFlight = flight?.slot === s.slot;
          return (
            <div
              key={s.slot}
              className="flex flex-col overflow-hidden rounded-3xl border border-border bg-pp-surface"
              data-testid={`admin-winner-slot-${s.slot}`}
            >
              <div className="relative aspect-square w-full bg-pp-surface-2">
                {s.occupant?.photo_url ? (
                  <img
                    src={s.occupant.photo_url}
                    alt={s.occupant.dog_name}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    data-testid={`admin-winner-slot-${s.slot}-photo`}
                  />
                ) : (
                  <div
                    className="flex h-full w-full items-center justify-center text-xs uppercase tracking-wider text-muted-foreground"
                    data-testid={`admin-winner-slot-${s.slot}-empty`}
                  >
                    Empty
                  </div>
                )}
                <div
                  className="absolute left-3 top-3 rounded-full bg-pp-purple px-3 py-1 text-xs font-bold uppercase tracking-wider text-white"
                  data-testid={`admin-winner-slot-${s.slot}-badge`}
                >
                  {slotBadgeLabel(s.rank)}
                </div>
              </div>
              <div className="flex flex-1 flex-col gap-3 p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  {s.slot}
                </div>
                {s.occupant && (
                  <div
                    className="font-heading text-lg font-bold text-pp-text"
                    data-testid={`admin-winner-slot-${s.slot}-name`}
                  >
                    {s.occupant.dog_name}
                  </div>
                )}
                <div className="mt-auto flex flex-wrap gap-2">
                  <Button
                    type="button"
                    onClick={() =>
                      setPickerOpen({ slotName: s.slot, kind: "populate" })
                    }
                    disabled={inFlight}
                    className="rounded-full bg-pp-purple text-white hover:bg-pp-purple-dark"
                    data-testid={`admin-winner-slot-${s.slot}-populate-btn`}
                  >
                    {inFlight && flight.action === "populate" ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    {isEmpty ? "Populate" : "Replace"}
                  </Button>
                  {!isEmpty && (
                    <>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() =>
                          setPickerOpen({ slotName: s.slot, kind: "swap" })
                        }
                        disabled={inFlight}
                        className="rounded-full"
                        data-testid={`admin-winner-slot-${s.slot}-swap-btn`}
                      >
                        <ArrowLeftRight className="mr-2 h-4 w-4" />
                        Swap
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleRemove(s.slot)}
                        disabled={inFlight}
                        className="rounded-full"
                        data-testid={`admin-winner-slot-${s.slot}-remove-btn`}
                      >
                        {inFlight && flight.action === "remove" ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <XIcon className="mr-2 h-4 w-4" />
                        )}
                        Remove
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Populate picker */}
      {pickerOpen?.kind === "populate" && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="admin-winner-slots-populate-modal"
        >
          <div className="max-h-[80vh] w-full max-w-3xl overflow-hidden rounded-3xl bg-pp-surface shadow-xl">
            <div className="flex items-center justify-between border-b border-border p-4">
              <h2 className="font-heading text-lg font-bold text-pp-text">
                Pick an entry for {pickerOpen.slotName}
              </h2>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setPickerOpen(null)}
                className="rounded-full"
                data-testid="admin-winner-slots-populate-modal-close"
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </div>
            <div className="max-h-[65vh] overflow-y-auto p-4">
              <p className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
                <Info className="h-3 w-3" />
                Ineligible entries are shown greyed out. Approve
                entries via Moderation to enable them. A "Consent
                missing" chip indicates a tamper/legacy record
                that skipped the submission consent form —
                investigate before assigning.
              </p>
              <ul className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {entries.length === 0 && (
                  <li
                    className="col-span-full py-8 text-center text-sm text-muted-foreground"
                    data-testid="admin-winner-slots-picker-empty"
                  >
                    No entries in this contest yet.
                  </li>
                )}
                {entries.map((e) => {
                  const disabled = !e.is_eligible;
                  return (
                    <li key={e.entry_id}>
                      <button
                        type="button"
                        onClick={() =>
                          !disabled &&
                          handlePopulate(pickerOpen.slotName, e.entry_id)
                        }
                        disabled={disabled}
                        title={
                          disabled
                            ? e.ineligible_reason === "Not approved"
                              ? "This entry hasn't cleared moderation yet — approve it first from the Moderation queue."
                              : "This entry has no consent_given record — it skipped the submission consent form. Investigate before assigning."
                            : `Populate ${pickerOpen.slotName} with ${e.dog_name}`
                        }
                        className={
                          "group flex w-full flex-col overflow-hidden rounded-2xl border border-border bg-pp-surface-2 text-left transition-transform hover:scale-[1.01] focus:outline-none focus-visible:ring-2 focus-visible:ring-pp-purple " +
                          (disabled ? "cursor-not-allowed opacity-40" : "")
                        }
                        data-testid={`admin-winner-slots-picker-entry-${e.entry_id}`}
                      >
                        <div className="relative aspect-square w-full bg-pp-surface">
                          {e.photo_url && (
                            <img
                              src={e.photo_url}
                              alt={e.dog_name || ""}
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          )}
                          {disabled && (
                            <span
                              className="absolute left-2 top-2 rounded-full bg-pp-surface px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-orange"
                              data-testid={`admin-winner-slots-picker-entry-${e.entry_id}-reason`}
                            >
                              {e.ineligible_reason}
                            </span>
                          )}
                        </div>
                        <div className="p-2 text-sm font-semibold text-pp-text">
                          {e.dog_name}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Swap picker */}
      {pickerOpen?.kind === "swap" && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="admin-winner-slots-swap-modal"
        >
          <div className="w-full max-w-md rounded-3xl bg-pp-surface p-6 shadow-xl">
            <h2 className="font-heading text-lg font-bold text-pp-text">
              Swap {pickerOpen.slotName} with…
            </h2>
            <ul className="mt-4 grid grid-cols-2 gap-2">
              {otherSlots.map((o) => (
                <li key={o.slot}>
                  <button
                    type="button"
                    onClick={() => handleSwap(pickerOpen.slotName, o.slot)}
                    className="w-full rounded-full border border-border bg-pp-surface-2 px-3 py-2 text-sm font-semibold text-pp-text hover:bg-pp-purple hover:text-white"
                    data-testid={`admin-winner-slots-swap-target-${o.slot}`}
                  >
                    {slotBadgeLabel(o.rank)} · {o.slot}
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPickerOpen(null)}
                className="rounded-full"
                data-testid="admin-winner-slots-swap-cancel"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
